import React, { useMemo, useState } from "react";
import {
    Search,
    SlidersHorizontal,
    Cpu,
    Calendar,
    Info,
} from "lucide-react";
import Navbar from "../components/landingPage/Navbar";

const CATEGORIES = ["All", "3D Printing", "CNC", "Laser Cutting", "PCB", "Moulding", "Robotics"];

const MACHINES = [
    {
        id: "M-3DP-01",
        name: "Industrial FDM 3D Printer",
        category: "3D Printing",
        make: "Stratasys F770",
        centre: "APIC Visakhapatnam",
        rate: 850,
        status: "Available",
    },
    {
        id: "M-CNC-05",
        name: "5-Axis CNC Machining Centre",
        category: "CNC",
        make: "DMG Mori DMU 50",
        centre: "APIC Vijayawada",
        rate: 2400,
        status: "Running",
    },
    {
        id: "M-LSR-02",
        name: "Fiber Laser Cutter 3kW",
        category: "Laser Cutting",
        make: "Trumpf TruLaser 3030",
        centre: "APIC Tirupati",
        rate: 1600,
        status: "Available",
    },
    {
        id: "M-PCB-03",
        name: "PCB Prototyping Mill",
        category: "PCB",
        make: "LPKF ProtoMat S104",
        centre: "APIC Kurnool",
        rate: 1100,
        status: "Available",
    },
    {
        id: "M-MLD-01",
        name: "Injection Moulding Unit",
        category: "Moulding",
        make: "Arburg Allrounder 270",
        centre: "APIC Guntur",
        rate: 3200,
        status: "Maintenance",
    },
    {
        id: "M-ROB-04",
        name: "6-Axis Robotic Arm Cell",
        category: "Robotics",
        make: "KUKA KR 10 R1100",
        centre: "APIC Nellore",
        rate: 1900,
        status: "Running",
    },
];

const STATUS_STYLES = {
    Available: "bg-green-100 text-green-700",
    Running: "bg-blue-100 text-blue-700",
    Maintenance: "bg-amber-100 text-amber-700",
};

function MachineCard({ machine }) {
    return (
        <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-start justify-between">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
                    <Cpu className="h-5 w-5" />
                </span>
                <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[machine.status]}`}>
                    {machine.status}
                </span>
            </div>

            <h3 className="text-base font-bold text-gray-900">{machine.name}</h3>
            <p className="mt-0.5 text-xs text-indigo-500">{machine.id}</p>

            <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                    <span className="text-gray-500">Technology</span>
                    <span className="font-medium text-gray-700">{machine.category}</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-gray-500">Make</span>
                    <span className="font-medium text-gray-700">{machine.make}</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-gray-500">Centre</span>
                    <span className="font-medium text-gray-700">{machine.centre}</span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-gray-500">Hourly rate</span>
                    <span className="font-semibold text-indigo-600">₹{machine.rate.toLocaleString("en-IN")}/hr</span>
                </div>
            </div>

            <div className="mt-5 flex items-center gap-2">
                <button className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 py-2.5 text-sm font-semibold text-white transition hover:opacity-90">
                    <Calendar className="h-4 w-4" />
                    Book
                </button>
                <button className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">
                    Details
                </button>
            </div>
        </div>
    );
}

export default function MachineMarketplace() {
    const [query, setQuery] = useState("");
    const [category, setCategory] = useState("All");

    const filtered = useMemo(() => {
        return MACHINES.filter((m) => {
            const matchesQuery =
                query.trim() === "" ||
                [m.name, m.make, m.centre, m.category].some((field) =>
                    field.toLowerCase().includes(query.trim().toLowerCase())
                );
            const matchesCategory = category === "All" || m.category === category;
            return matchesQuery && matchesCategory;
        });
    }, [query, category]);

    return (
        <div className="min-h-screen w-full bg-gray-50">
            <Navbar />

            {/* Hero */}
            <div className="bg-gradient-to-r from-[#0f1c3f] to-[#5b21b6] px-8 pb-20 pt-12 text-white">
                <span className="inline-flex rounded-full border border-white/30 px-3 py-1 text-xs font-semibold">
                    Machine Marketplace
                </span>
                <h1 className="mt-4 text-4xl font-extrabold sm:text-5xl">
                    {MACHINES.length * 57}+ Machines. One platform.
                </h1>
                <p className="mt-3 max-w-xl text-white/80">
                    Book industrial 3D printers, CNC machines, laser cutters, PCB prototypers and more.
                </p>

                {/* Search card */}
                <div className="mt-8 rounded-2xl bg-white p-4 shadow-lg">
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search machine, technology or make..."
                                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-gray-300 focus:bg-white"
                            />
                        </div>
                        <button className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                            <SlidersHorizontal className="h-4 w-4" />
                            Filters
                        </button>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCategory(c)}
                                className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${category === c
                                    ? "bg-indigo-600 text-white"
                                    : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                                    }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Machine grid */}
            <div className="-mt-10 px-8 pb-10">
                <p className="mb-4 text-sm text-gray-500">
                    {filtered.length} of {MACHINES.length} machines
                </p>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {filtered.map((m) => (
                        <MachineCard key={m.id} machine={m} />
                    ))}
                </div>

                {filtered.length === 0 && (
                    <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white py-16 text-center">
                        <Info className="mb-3 h-6 w-6 text-gray-400" />
                        <p className="text-sm text-gray-500">No machines match your search.</p>
                    </div>
                )}
            </div>
        </div>
    );
}